import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Send, AlertTriangle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ExportMenu from "../../components/ExportMenu";

const eur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(v) || 0);

export default function DefaultersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [grand, setGrand] = useState(0);
  const [minAmount, setMinAmount] = useState(0);
  const [minDays, setMinDays] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    const { data } = await http.get(`/defaulters?min_amount=${minAmount}&min_days=${minDays}`);
    setRows(data.items || []); setGrand(data.grand_total || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minAmount, minDays]);

  const remind = async (fid) => {
    try { await http.post(`/defaulters/${fid}/remind`); toast.success(t("defaulters.reminderSent")); load(); }
    catch { toast.error("Failed"); }
  };
  const bulkRemind = async () => {
    if (selected.size === 0) return;
    try {
      await http.post("/defaulters/bulk-remind", { family_ids: Array.from(selected) });
      toast.success(t("defaulters.bulkSent", { n: selected.size })); setSelected(new Set()); load();
    } catch { toast.error("Failed"); }
  };
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <PageChrome title={t("defaulters.title")} subtitle={t("defaulters.subtitle")} testId="defaulters-page">
      <div className="flex flex-wrap gap-3 items-center mb-3">
        <div className="px-3 py-2 bg-rose-50 border border-rose-200 rounded text-sm">
          <strong className="text-[#C8102E]">{eur(grand)}</strong> · {rows.length} {t("defaulters.familiesOverdue")}
        </div>
        <label className="text-xs flex items-center gap-1">{t("defaulters.minAmount")}: <Input type="number" className="w-24" value={minAmount} onChange={(e)=>setMinAmount(Number(e.target.value)||0)}/></label>
        <label className="text-xs flex items-center gap-1">{t("defaulters.minDays")}: <Input type="number" className="w-20" value={minDays} onChange={(e)=>setMinDays(Number(e.target.value)||0)}/></label>
        <div className="ml-auto flex gap-2">
          {selected.size > 0 && <Button onClick={bulkRemind} className="bg-[#C8102E] hover:bg-[#a40c25]" data-testid="defaulters-bulk-remind"><Send className="h-4 w-4 mr-1"/>{t("defaulters.bulkRemind", { n: selected.size })}</Button>}
          <ExportMenu resource="defaulters" extraParams={{ min_amount: minAmount, min_days: minDays }} filename="defaulters" testId="defaulters-export"/>
        </div>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="defaulters-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="w-8 px-3 py-2"></th>
              <th className="text-left px-3 py-2">{t("defaulters.family")}</th>
              <th className="text-right px-3 py-2">{t("defaulters.outstanding")}</th>
              <th className="text-right px-3 py-2">{t("defaulters.nInvoices")}</th>
              <th className="text-right px-3 py-2">{t("defaulters.daysOverdue")}</th>
              <th className="text-left px-3 py-2">{t("defaulters.oldest")}</th>
              <th className="text-left px-3 py-2">{t("defaulters.lastReminder")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map(r => (
              <tr key={r.family_id} className="hover:bg-slate-50" data-testid={`defaulter-row-${r.family_id}`}>
                <td className="px-3 py-2"><Checkbox checked={selected.has(r.family_id)} onCheckedChange={()=>toggle(r.family_id)}/></td>
                <td className="px-3 py-2 font-medium">{r.family_name}</td>
                <td className="px-3 py-2 text-right font-semibold text-[#C8102E]">{eur(r.outstanding_total)}</td>
                <td className="px-3 py-2 text-right">{r.n_overdue}</td>
                <td className="px-3 py-2 text-right">{r.days_overdue > 60 ? <span className="text-[#C8102E] font-semibold">{r.days_overdue}d <AlertTriangle className="inline h-3 w-3"/></span> : `${r.days_overdue}d`}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.oldest_invoice}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.last_reminder_at ? r.last_reminder_at.slice(0,10) : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={()=>remind(r.family_id)} data-testid={`defaulter-remind-${r.family_id}`}><Send className="h-4 w-4 mr-1"/>{t("defaulters.remind")}</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">{t("defaulters.empty")}</td></tr>}
          </tbody>
        </table>
      </Card>
    </PageChrome>
  );
}
